import { Badge } from "@/components/ui/badge";
import { CustomerTile } from "@/components/molecules/customer-tile";
import type { Customer } from "@/features/customers/types";
import type { ColumnDef } from "@tanstack/react-table";
import { Action } from "./columns-action";
import { useDateFormat, useTranslation } from "@/features/utils/i18n";

export const useCustomersColumns = (): ColumnDef<Customer>[] => {
  const { t } = useTranslation("customers");
  const { format } = useDateFormat();

  return [
    {
      accessorKey: "name",
      header: () => t("table.customer"),
      enableSorting: true,
      cell: ({ row }) => {
        return (
          <CustomerTile
            customerId={row.original.id}
            fallback={t("table.unnamedCustomer")}
          />
        );
      },
    },
    {
      accessorKey: "company",
      header: () => t("table.company"),
      enableSorting: false, // Disable sorting on JSON field for now
      cell: ({ row }) => {
        const customer = row.original;
        const company = customer.organization?.company;
        if (!company) return <span className="text-muted-foreground">-</span>;
        return <span>{company}</span>;
      },
    },
    {
      accessorKey: "info",
      header: () => t("table.contactInfo"),
      enableSorting: false, // Disable sorting on computed column
      cell: ({ row }) => {
        const customer = row.original;
        const emailCount = customer.addresses?.email?.length || 0;
        const phoneCount = customer.addresses?.phone?.length || 0;
        const addressCount = customer.addresses?.postal?.length || 0;

        const infoBadges = [];
        if (emailCount > 1)
          infoBadges.push(`${emailCount} ${t("table.emails")}`);
        if (phoneCount > 1)
          infoBadges.push(`${phoneCount} ${t("table.phones")}`);
        if (addressCount > 0)
          infoBadges.push(
            `${addressCount} ${addressCount > 1 ? t("table.addresses") : t("table.address")}`,
          );

        // The primary e-mail and phone are already under the name, in the
        // customer tile: this column only says what lies beyond them.
        if (infoBadges.length === 0) return null;

        return (
          <div className="flex flex-wrap gap-1">
            {infoBadges.map((badge, i) => (
              <Badge key={i} variant="secondary" className="text-xs">
                {badge}
              </Badge>
            ))}
          </div>
        );
      },
    },
    {
      accessorKey: "created_at",
      header: () => t("table.added"),
      cell: ({ row }) => {
        const createdAt = row.original.created_at;
        if (!createdAt) return null;
        return (
          <span className="text-sm text-muted-foreground">
            {format(createdAt, "PP")}
          </span>
        );
      },
    },
    {
      id: "actions",
      header: () => <div className="text-right">{t("table.actions")}</div>,
      cell: ({ row }) => <Action customer={row.original} />,
    },
  ];
};
